/*
 * Yutrients cart plan selector — per-line subscription cadence picker.
 *
 * Decision 0005: the customer picks their plan in the CART (not just the
 * PDP). Each Formula line gets a three-way selector:
 *
 *   - One-time       — no selling plan, quantity 1.
 *   - Monthly        — the 1-month Recharge plan, quantity 1.
 *   - 3-month bundle — the 3-month Recharge plan, quantity 3
 *                      (all 3 tins ship together, founder-confirmed).
 *
 * The selector markup is rendered by sections/main-cart-items.liquid only
 * on lines for the Formula parent product (handle 'custom-blend').
 * Plan IDs reach us via data-* attributes on each line's selector wrapper,
 * populated from the cart section settings (recharge_plan_1mo / 3mo). When a
 * plan ID is blank the corresponding option isn't rendered by Liquid, so we
 * never need to handle a missing-plan click here — One-time is always present.
 *
 * On selecting an option we POST /cart/change.js keyed by the line ITEM KEY
 * (robust against re-ordering) with { id, quantity, selling_plan }, then
 * re-fetch the main-cart-items section HTML and swap the .js-contents — the
 * same refresh pattern cart.js uses. On failure we surface an inline error
 * and revert the active option.
 *
 * We bind via event delegation on a stable ancestor (document) so the
 * controller keeps working after the section re-renders and replaces the
 * selector DOM.
 */

(function () {
  'use strict';

  const SELECTOR_OPTION = '[data-yt-cart-plan-option]';
  const SELECTOR_WRAP = '[data-yt-cart-plan]';
  const CONTENTS_SELECTOR = '#main-cart-items .js-contents';
  // Cap every network call so a hung request can't leave `busy` stuck true,
  // which would permanently dead-lock the selector on every cart line.
  const FETCH_TIMEOUT_MS = 12000;

  let busy = false;

  /**
   * fetch() with an AbortController timeout. Rejects with a clear error on
   * timeout so the caller can revert state and surface a retry message
   * instead of spinning forever.
   */
  function fetchWithTimeout(url, options) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);
    const opts = Object.assign({}, options, { signal: controller.signal });
    return fetch(url, opts)
      .catch((err) => {
        if (err && err.name === 'AbortError') {
          throw new Error('cart request timed out');
        }
        throw err;
      })
      .finally(() => clearTimeout(timer));
  }

  /**
   * POST /cart/change.js for one line. Returns the parsed cart JSON on
   * success; throws on any non-OK response.
   */
  async function changeLine(lineKey, quantity, sellingPlan) {
    const body = {
      id: lineKey,
      quantity: quantity,
    };
    // Shopify treats selling_plan omitted-or-null as "no plan" (one-time).
    body.selling_plan = sellingPlan || null;

    const res = await fetchWithTimeout('/cart/change.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(body),
    });

    if (!res.ok) {
      const text = await res.text().catch(() => '');
      throw new Error('cart/change.js failed: ' + res.status + ' ' + text.slice(0, 160));
    }
    return res.json();
  }

  function cartUrl() {
    return window.routes && window.routes.cart_url ? window.routes.cart_url : '/cart';
  }

  /**
   * Re-fetch a rendered section by its section id and swap the inner HTML of
   * `selector` from the response into the live `targetEl`. Mirrors the
   * section-render refresh pattern in cart.js. Returns true on success.
   */
  async function refreshSection(sectionId, selector) {
    const res = await fetchWithTimeout(cartUrl() + '?section_id=' + encodeURIComponent(sectionId));
    if (!res.ok) throw new Error('section refresh failed: ' + res.status);
    const text = await res.text();
    const doc = new DOMParser().parseFromString(text, 'text/html');
    const source = doc.querySelector(selector);
    const target = document.querySelector(selector);
    if (source && target) {
      target.innerHTML = source.innerHTML;
      return true;
    }
    return false;
  }

  /**
   * Refresh BOTH the cart line items AND the footer (subtotal/buttons) so the
   * line prices, the selector active-state and the cart total all re-render
   * straight from Liquid after a plan change. We read each section's id from
   * its live `data-id` attribute, exactly like cart.js does. If we can't
   * resolve a section we fall back to a full reload so the customer always
   * sees a consistent, correct cart.
   */
  async function refreshCartSection() {
    const itemsEl = document.getElementById('main-cart-items');
    const footerEl = document.getElementById('main-cart-footer');

    const itemsId = itemsEl && itemsEl.dataset ? itemsEl.dataset.id : null;
    if (!itemsId) {
      window.location.reload();
      return;
    }

    const okItems = await refreshSection(itemsId, CONTENTS_SELECTOR);
    if (!okItems) {
      window.location.reload();
      return;
    }

    // Footer is a separate section — refresh it so the subtotal reflects the
    // new plan/quantity. A failure here is non-fatal (items already updated),
    // so we reload to keep the total truthful.
    const footerId = footerEl && footerEl.dataset ? footerEl.dataset.id : null;
    if (footerId) {
      const okFooter = await refreshSection(footerId, '#main-cart-footer .js-contents');
      if (!okFooter) window.location.reload();
    }
  }

  function setBusyState(wrap, isBusy) {
    if (!wrap) return;
    wrap.classList.toggle('yt-cart-plan--busy', isBusy);
    wrap.setAttribute('aria-busy', isBusy ? 'true' : 'false');
  }

  function showError(wrap, message) {
    if (!wrap) return;
    const errorEl = wrap.querySelector('[data-yt-cart-plan-error]');
    if (errorEl) {
      errorEl.textContent = message;
      errorEl.hidden = false;
    }
  }

  function clearError(wrap) {
    if (!wrap) return;
    const errorEl = wrap.querySelector('[data-yt-cart-plan-error]');
    if (errorEl) {
      errorEl.textContent = '';
      errorEl.hidden = true;
    }
  }

  function setActiveOption(wrap, activeOption) {
    const options = wrap.querySelectorAll(SELECTOR_OPTION);
    options.forEach((opt) => {
      const isActive = opt === activeOption;
      opt.classList.toggle('yt-cart-plan__option--selected', isActive);
      opt.setAttribute('aria-checked', isActive ? 'true' : 'false');
      const radio = opt.querySelector('input[type="radio"]');
      if (radio) radio.checked = isActive;
    });
  }

  async function handleSelect(option) {
    if (busy) return;

    const wrap = option.closest(SELECTOR_WRAP);
    if (!wrap) return;

    // Already the active plan → nothing to do.
    if (option.classList.contains('yt-cart-plan__option--selected')) return;

    const lineKey = wrap.getAttribute('data-line-key');
    const quantity = parseInt(option.getAttribute('data-yt-plan-qty'), 10) || 1;
    const sellingPlan = option.getAttribute('data-yt-plan-id') || '';

    if (!lineKey) {
      showError(wrap, 'Could not update this item — try refreshing the page.');
      return;
    }

    // Remember what was selected so we can revert the highlight on failure.
    const previouslySelected = wrap.querySelector('.yt-cart-plan__option--selected');

    busy = true;
    clearError(wrap);
    setActiveOption(wrap, option);
    setBusyState(wrap, true);

    try {
      await changeLine(lineKey, quantity, sellingPlan);
    } catch (err) {
      // The change itself failed → nothing changed server-side, so revert
      // the highlight and surface an inline retry message.
      if (previouslySelected) {
        setActiveOption(wrap, previouslySelected);
      }
      setBusyState(wrap, false);
      showError(wrap, 'Could not update your plan. Please try again.');
      // eslint-disable-next-line no-console
      console.error('[yt-cart-plan-selector]', err);
      busy = false;
      return;
    }

    try {
      await refreshCartSection();
      // After refresh the DOM is replaced; busy clears below. No need to
      // touch the (now stale) wrap reference further.
    } catch (err) {
      // The plan DID change server-side but the re-render failed (e.g. a
      // hung section fetch). Reverting would lie about the cart state, so
      // reload to guarantee the customer sees a consistent, correct cart.
      // eslint-disable-next-line no-console
      console.error('[yt-cart-plan-selector] refresh failed, reloading', err);
      window.location.reload();
      return;
    } finally {
      busy = false;
    }
  }

  // Event delegation on `change` — survives the section re-renders that
  // replace the selector DOM. Each option is a <label> wrapping its radio,
  // so a click/tap/keyboard-activation anywhere on the card checks the radio
  // and fires `change` here. Binding only on `change` (not also `click`)
  // avoids any double-fire ambiguity.
  //
  // We bind in the CAPTURE phase and stopPropagation for our own radios so
  // the change never reaches the theme's <cart-items> bubble-phase listener
  // (cart.js), which would otherwise try to validate our radio as a quantity
  // input (parseInt('1mo') === NaN). Capture runs document-first, so we can
  // halt the event before it descends to <cart-items>. Non-selector changes
  // (the real quantity inputs) are untouched and propagate normally.
  document.addEventListener(
    'change',
    function (event) {
      const target = event.target;
      if (!target.matches || !target.matches('[data-yt-cart-plan] input[type="radio"]')) return;
      event.stopPropagation();
      if (!target.checked) return;
      const option = target.closest(SELECTOR_OPTION);
      if (option) handleSelect(option);
    },
    true
  );

  // Reveal the collapsed one-time bubble (mirrors the Formula PDP). Delegated
  // on document so it survives the section re-renders that replace the DOM.
  // One-shot per render: clicking adds .is-revealed to the options wrapper,
  // which CSS uses to hide the link and show the one-time bubble. The customer
  // still has to click the bubble itself to switch their plan.
  document.addEventListener('click', function (event) {
    const link =
      event.target.closest && event.target.closest('[data-yt-cart-plan-reveal-onetime]');
    if (!link) return;
    event.preventDefault();
    const wrap = link.closest(SELECTOR_WRAP);
    const options = wrap && wrap.querySelector('.yt-cart-plan__options');
    if (options) options.classList.add('is-revealed');
  });
})();
